import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DropdownSearchCombobox } from '@/app/administration/campaign-management/create/_components/DropdownSearchCombobox';
import { getSorLookup } from '@/lib/api/client';
import { KbConfigResponse, FolderConfig, LookupOption } from '@/lib/api/types';
import { cn } from '@/lib/utils';
import { toast } from 'react-hot-toast';

const inputStyles =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors " +
  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground " +
  "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring " +
  "disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

interface DynamicKbFormProps {
  kbConfig: KbConfigResponse;
  agentType: string;
  values: Record<string, string>;
  onChange: (key: string, value: string, kbTypeId?: string) => void;
  disabled?: boolean;
}

interface FieldState {
  value: string;
  kbTypeId: string;
  options: LookupOption[];
  loading: boolean;
  selectedOption?: LookupOption; // Store the selected option
}

export const DynamicKbForm: React.FC<DynamicKbFormProps> = ({
  kbConfig,
  agentType,
  values,
  onChange,
  disabled = false
}) => {
  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>({});
  const loadedFieldsRef = useRef<Set<string>>(new Set());
  const searchTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
  const initializedRef = useRef<boolean>(false);

  // Initialize field states - only for main field
  useEffect(() => {
    const initializeFields = (config: KbConfigResponse | FolderConfig) => {
      const newStates: Record<string, FieldState> = {};
      
      // Only handle main key, skip folders
      if ('key' in config && config.key) {
        const key = config.key;
        const fieldValue = values[key] || '';
        
        newStates[key] = {
          value: fieldValue,
          kbTypeId: '',
          options: [],
          loading: false,
          selectedOption: undefined
        };
      }
      
      setFieldStates(newStates);
    };

    // Reset loaded fields and initialization flag when kbConfig or values change
    loadedFieldsRef.current.clear();
    initializedRef.current = false;
    initializeFields(kbConfig);
  }, [kbConfig, values]);

  // Load lookup options for a field
  const loadLookupOptions = useCallback(async (
    key: string, 
    lookupKey?: string,
    searchValue?: string
  ) => {
    // Create a unique identifier for this lookup call
    const lookupId = lookupKey ? `${key}-${lookupKey}` : key;
    
    // For searchable fields, only prevent duplicate calls if no search value
    // Allow calls with different search values
    if (!searchValue && loadedFieldsRef.current.has(lookupId)) {
      return;
    }

    if (!searchValue) {
      loadedFieldsRef.current.add(lookupId);
    }

    setFieldStates(prev => {
      // Check if already loading
      if (prev[key]?.loading) {
        return prev;
      }
      
      return {
        ...prev,
        [key]: { ...prev[key], loading: true }
      };
    });

    try {
      // Use the field key as the type parameter, not the kbType
      const response = await getSorLookup(agentType, key, lookupKey, undefined, undefined, searchValue);
      const options = response.data?.data || [];
      
      setFieldStates(prev => {
        const currentValue = prev[key]?.value;
        const selectedOption = currentValue ? options.find(opt => opt.value === currentValue) : undefined;
        
        return {
          ...prev,
          [key]: { 
            ...prev[key], 
            options,
            loading: false,
            // Set selectedOption if we found a match
            selectedOption: selectedOption || prev[key]?.selectedOption
          }
        };
      });
    } catch (error) {
      toast.error(`Failed to load options for ${key}`);
      setFieldStates(prev => ({
        ...prev,
        [key]: { ...prev[key], loading: false }
      }));
      // Remove from loaded set on error so it can be retried
      if (!searchValue) {
        loadedFieldsRef.current.delete(lookupId);
      }
    }
  }, [agentType]);

  // Handle field value change - simplified for main field only
  const handleFieldChange = useCallback((key: string, value: string, selectedOption?: LookupOption) => {
    const kbTypeId = selectedOption?.id || '';
    // Convert value to lowercase
    const lowercaseValue = value.toLowerCase();
    
    setFieldStates(prev => ({
      ...prev,
      [key]: { 
        ...prev[key], 
        value: lowercaseValue,
        kbTypeId,
        selectedOption // Store the selected option
      }
    }));
    
    onChange(key, lowercaseValue, kbTypeId);
  }, [onChange]);

  // Load initial lookup options - only for main field
  // This effect runs ONCE after field states are initialized
  useEffect(() => {
    // Skip if already initialized
    if (initializedRef.current) {
      return;
    }

    const loadInitialOptions = (config: KbConfigResponse | FolderConfig) => {
      // Only handle main key, skip folders
      if ('key' in config && config.key && config.lookup && !config.lookupKey) {
        const key = config.key;
        
        // Only load if not already loaded
        if (!loadedFieldsRef.current.has(key)) {
          // ONLY load options for non-searchable fields
          // For searchable fields, wait for user to type 3+ characters
          if (!config.searchable) {
            loadLookupOptions(key);
            initializedRef.current = true;
          } else {
            initializedRef.current = true;
          }
        }
      }
    };

    // Only load initial options after field states are initialized
    if (Object.keys(fieldStates).length > 0) {
      loadInitialOptions(kbConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldStates]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(searchTimeoutRef.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Render a single field
  const renderField = (
    key: string,
    name: string,
    lookup: boolean = false,
    searchable: boolean = false,
    required: boolean = false,
    hasName: boolean = true
  ) => {
    // If name is not present in config, don't render the field at all
    if (!hasName) {
      return null;
    }
    
    const fieldState = fieldStates[key] || { value: '', kbTypeId: '', options: [], loading: false, selectedOption: undefined };
    
    if (lookup) {
      // Handle search change for searchable fields with debouncing
      const handleSearchChange = (searchTerm: string) => {
        // Clear any existing timeout for this field
        if (searchTimeoutRef.current[key]) {
          clearTimeout(searchTimeoutRef.current[key]);
        }

        // Only trigger API call if searchable is true and search term is 3+ characters
        if (searchable && searchTerm.length >= 3) {
          // Debounce the API call by 500ms
          searchTimeoutRef.current[key] = setTimeout(() => {
            loadLookupOptions(key, undefined, searchTerm);
          }, 500);
        } else if (searchable && searchTerm.length < 3) {
          // Clear options when search term is less than 3 characters
          setFieldStates(prev => ({
            ...prev,
            [key]: { ...prev[key], options: [] }
          }));
        }
      };

      // Get the selected option label - try from selectedOption first, then from current options
      let selectedLabel = '';
      if (fieldState.selectedOption) {
        selectedLabel = `${fieldState.selectedOption.value} - ${fieldState.selectedOption.name}`;
      } else if (fieldState.value && fieldState.options.length > 0) {
        const option = fieldState.options.find(opt => opt.value === fieldState.value);
        if (option) {
          selectedLabel = `${option.value} - ${option.name}`;
        }
      }

      // For display in read-only field: use selectedLabel if available, otherwise just the value
      const displayValue = selectedLabel || fieldState.value;

      return (
        <div key={key} className="space-y-2">
          <label className="text-sm font-medium">
            {name} {required && <span className="text-red-500">*</span>}
          </label>
          
          {/* Show selected value in read-only input if a value is selected */}
          {fieldState.value && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Selected Value</label>
              <input
                type="text"
                value={displayValue}
                className={cn(inputStyles, "bg-muted/50 cursor-not-allowed")}
                readOnly
                placeholder="No selection"
              />
            </div>
          )}
          
          {/* Only show the dropdown if no value is selected */}
          {!fieldState.value && (
            <div>
              <label className="text-xs text-muted-foreground">Select Value</label>
              <DropdownSearchCombobox
                value={fieldState.value}
                options={fieldState.options.map(opt => ({
                  label: `${opt.value} - ${opt.name}`,
                  value: opt.value
                }))}
                placeholder={fieldState.loading ? "Loading..." : searchable ? `Type at least 3 characters to search ${name}` : `Select ${name}`}
                disabled={disabled || fieldState.loading}
                isSearchable={searchable}
                isLoading={fieldState.loading}
                onSearchChange={searchable ? handleSearchChange : undefined}
                onChange={(value) => {
                  const selectedOption = fieldState.options.find(opt => opt.value === value);
                  handleFieldChange(key, value, selectedOption);
                }}
              />
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div key={key} className="space-y-2">
        <label className="text-sm font-medium">
          {name} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          value={fieldState.value}
          onChange={(e) => handleFieldChange(key, e.target.value)}
          className={cn(inputStyles)}
          placeholder={`Enter ${name}`}
          disabled={disabled}
        />
      </div>
    );
  };

  // Render fields - only show main field, not nested folders
  const renderFields = (config: KbConfigResponse | FolderConfig): React.ReactNode[] => {
    const fields: React.ReactNode[] = [];
    
    // Only handle main key, skip folders for the Knowledge Base Setup screen
    if ('key' in config && config.key) {
      // Check if name property exists in the config
      const hasName = !!(config.name && config.name.trim());
      
      fields.push(renderField(
        config.key,
        config.name || config.key,
        config.lookup,
        config.searchable || false,
        true,
        hasName
      ));
    }
    
    return fields;
  };

  return (
    <div className="space-y-4">
      {renderFields(kbConfig)}
    </div>
  );
};